/**
 * Typed RevenueCat webhook event catalog.
 *
 * RevenueCat exposes more event types than Brew needs for customer lifecycle
 * automation. Brew curates the 11 purchase, renewal, cancellation, billing,
 * pause, extension, and transfer events below.
 *
 * Hard schema invariant: every entry's payload schema includes
 * `{ key: 'email', type: 'string', required: true }`. RevenueCat's
 * `event.app_user_id` is normally YOUR app's user id, though some apps use an
 * email address there. Brew first reads
 * `event.subscriber_attributes.$email.value` (the canonical email source per
 * https://www.revenuecat.com/docs/customers/customer-attributes),
 * then accepts email-shaped current/original app user ids, walks `aliases[]`
 * for a defensive email fallback, and otherwise synthesizes the org-scoped
 * cross-provider placeholder.
 */

import type {
  InboundEventCategory,
  InboundEventMetadata,
} from '@/lib/integrations/inbound/types'
import type {
  IntegrationTriggerField,
  IntegrationTriggerPayloadSchema,
} from '@/lib/triggers/types'

type TriggerField = IntegrationTriggerField
type TriggerPayloadSchema = IntegrationTriggerPayloadSchema
export type SupportedRevenueCatEventType =
  | 'INITIAL_PURCHASE'
  | 'RENEWAL'
  | 'NON_RENEWING_PURCHASE'
  | 'PRODUCT_CHANGE'
  | 'CANCELLATION'
  | 'UNCANCELLATION'
  | 'EXPIRATION'
  | 'BILLING_ISSUE'
  | 'SUBSCRIPTION_PAUSED'
  | 'SUBSCRIPTION_EXTENDED'
  | 'TRANSFER'

export const SUPPORTED_REVENUECAT_EVENTS: ReadonlyArray<SupportedRevenueCatEventType> =
  [
    'INITIAL_PURCHASE',
    'RENEWAL',
    'NON_RENEWING_PURCHASE',
    'PRODUCT_CHANGE',
    'CANCELLATION',
    'UNCANCELLATION',
    'EXPIRATION',
    'BILLING_ISSUE',
    'SUBSCRIPTION_PAUSED',
    'SUBSCRIPTION_EXTENDED',
    'TRANSFER',
  ] as const

const SUPPORTED_REVENUECAT_EVENT_SET = new Set<string>(
  SUPPORTED_REVENUECAT_EVENTS
)

/**
 * Slim user-facing common preamble. RevenueCat event metadata
 * (`revenuecatEventId`, `revenuecatEventType`, `appUserId`,
 * `originalAppUserId`) is intentionally absent — the extractor still
 * emits those keys for the contact mapper to read. `store` and
 * `environment` ARE declared: marketers split on platform
 * (app_store vs play_store) and filter out sandbox events.
 */
const COMMON_FIELDS: Array<TriggerField> = [
  {
    key: 'email',
    type: 'string',
    required: true,
    description:
      'Recipient email from `event.subscriber_attributes.$email.value` (the canonical source), an email-shaped current/original app user id, or an email-shaped alias. Otherwise Brew uses `unknown+<app_user_id>@<orgId>.brew.local`.',
    exampleValue: 'alex@example.com',
  },
  {
    key: 'productId',
    type: 'string',
    required: false,
    description: 'Product id from the store when present.',
    exampleValue: 'pro_monthly',
  },
  {
    key: 'priceUsd',
    type: 'string',
    required: false,
    description:
      "Price in USD (RevenueCat's `event.price`). NOT the purchase-currency price — that is `pricePurchaseCurrency`.",
    exampleValue: '9.99',
  },
  {
    key: 'pricePurchaseCurrency',
    type: 'string',
    required: false,
    description:
      "Price in the customer's purchase currency (RevenueCat's `event.price_in_purchased_currency`). The currency code is on `currency`.",
    exampleValue: '9.99',
  },
  {
    key: 'currency',
    type: 'string',
    required: false,
    description: 'Currency code for `pricePurchaseCurrency` when present.',
    exampleValue: 'USD',
  },
  {
    key: 'store',
    type: 'string',
    required: false,
    description:
      'Store that processed the purchase when present — passed through from RevenueCat, e.g. `APP_STORE`, `PLAY_STORE`, `MAC_APP_STORE`, `AMAZON`, `STRIPE`, `RC_BILLING`, `PADDLE`, `ROKU`, `PROMOTIONAL`, `TEST_STORE`. RevenueCat can add new stores, so match on the values you care about rather than assuming a closed set.',
    exampleValue: 'APP_STORE',
  },
  {
    key: 'environment',
    type: 'string',
    required: false,
    description:
      'RevenueCat environment (`PRODUCTION` or `SANDBOX`). Filter out sandbox events from live automations.',
    exampleValue: 'PRODUCTION',
  },
  {
    key: 'createdAt',
    type: 'int',
    required: false,
    description:
      'Unix timestamp (seconds) derived from `event.event_timestamp_ms` when available.',
    exampleValue: 1_710_000_000,
  },
]

const PRODUCT_CHANGE_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'previousProductId',
    type: 'string',
    required: false,
    description: 'Previous product id when RevenueCat reports it.',
    exampleValue: 'starter_monthly',
  },
  {
    key: 'newProductId',
    type: 'string',
    required: false,
    description: 'New product id after the change.',
    exampleValue: 'pro_monthly',
  },
]

const CANCELLATION_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'cancelReason',
    type: 'string',
    required: false,
    description:
      'RevenueCat cancellation reason when present (`UNSUBSCRIBE`, `BILLING_ERROR`, `DEVELOPER_INITIATED`, `PRICE_INCREASE`, `CUSTOMER_SUPPORT`, `UNKNOWN`).',
    exampleValue: 'CUSTOMER_SUPPORT',
  },
]

const EXPIRATION_EXTRA_FIELDS: Array<TriggerField> = [
  {
    key: 'expirationReason',
    type: 'string',
    required: false,
    description:
      'RevenueCat expiration reason when present (`UNSUBSCRIBE`, `BILLING_ERROR`, `DEVELOPER_INITIATED`, `PRICE_INCREASE`, `CUSTOMER_SUPPORT`, `UNKNOWN`, `SUBSCRIPTION_PAUSED`).',
    exampleValue: 'BILLING_ERROR',
  },
]

const TRANSFER_EXTRA_FIELDS: Array<TriggerField> = []

const NO_EXTRAS: ReadonlyArray<TriggerField> = []

function buildPayloadSchema(
  extras: ReadonlyArray<TriggerField>
): TriggerPayloadSchema {
  return {
    type: 'object',
    fields: [...COMMON_FIELDS, ...extras],
  }
}

function buildSamplePayload(
  _eventType: SupportedRevenueCatEventType,
  payload: Record<string, string | number | boolean | null>
): Record<string, string | number | boolean | null> {
  return {
    email: 'alex@example.com',
    store: 'APP_STORE',
    environment: 'PRODUCTION',
    ...payload,
  }
}

export const REVENUECAT_EVENT_CATEGORIES: ReadonlyArray<InboundEventCategory> =
  [
    { id: 'purchase', label: 'Purchase' },
    { id: 'subscription', label: 'Subscription' },
    { id: 'billing', label: 'Billing' },
  ]

export const REVENUECAT_EVENT_METADATA: Array<InboundEventMetadata> = [
  {
    eventType: 'INITIAL_PURCHASE',
    title: 'RevenueCat - Initial Purchase',
    description:
      'Fires when a new subscription starts, including a trial start. One-time purchases use `NON_RENEWING_PURCHASE`.',
    category: 'purchase',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('INITIAL_PURCHASE', {
      productId: 'pro_monthly',
      priceUsd: '9.99',
      currency: 'USD',
      createdAt: 1_710_000_000,
    }),
    useCases: [
      {
        title: 'Welcome to premium',
        description: 'Greet new paid customers with onboarding next steps.',
      },
      {
        title: 'Onboarding sequence',
        description: 'Kick off a multi-day welcome drip.',
      },
    ],
  },
  {
    eventType: 'RENEWAL',
    title: 'RevenueCat - Renewal',
    description:
      'Fires when a subscription renews or a lapsed subscriber resubscribes.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('RENEWAL', {
      productId: 'pro_monthly',
      priceUsd: '9.99',
      currency: 'USD',
    }),
    useCases: [
      {
        title: 'Receipt',
        description: 'Confirm the renewal + charge details.',
      },
      {
        title: 'Milestone email',
        description: '"You’ve been a Pro for 1 year!" loyalty messaging.',
      },
    ],
  },
  {
    eventType: 'NON_RENEWING_PURCHASE',
    title: 'RevenueCat - One-time Purchase',
    description: 'Fires when a non-renewing / consumable product is purchased.',
    category: 'purchase',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('NON_RENEWING_PURCHASE', {
      productId: 'coin_pack_large',
      priceUsd: '19.99',
      currency: 'USD',
    }),
    useCases: [
      {
        title: 'Thank-you email',
        description: 'Confirm the purchase + suggest related items.',
      },
    ],
  },
  {
    eventType: 'PRODUCT_CHANGE',
    title: 'RevenueCat - Product Change',
    description:
      'Fires when a subscriber requests a product change; the new product may take effect later.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(PRODUCT_CHANGE_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('PRODUCT_CHANGE', {
      previousProductId: 'starter_monthly',
      newProductId: 'pro_monthly',
    }),
    useCases: [
      {
        title: 'Tier-change confirmation',
        description: 'Confirm the new plan + outline what changed.',
      },
    ],
  },
  {
    eventType: 'CANCELLATION',
    title: 'RevenueCat - Cancellation',
    description:
      'Fires when a subscription or non-renewing purchase is cancelled or refunded. Do not infer current entitlement access from this event alone.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(CANCELLATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('CANCELLATION', {
      cancelReason: 'CUSTOMER_SUPPORT',
    }),
    useCases: [
      {
        title: 'Win-back',
        description:
          'Send a discount or appeal-to-stay during the grace period.',
      },
      {
        title: 'Exit survey',
        description: 'Ask why they cancelled to inform retention efforts.',
      },
    ],
  },
  {
    eventType: 'UNCANCELLATION',
    title: 'RevenueCat - Uncancellation',
    description:
      'Fires when a user reinstates a cancelled subscription before it expires.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('UNCANCELLATION', {}),
    useCases: [
      {
        title: 'Welcome back',
        description: 'Thank the user for sticking around.',
      },
    ],
  },
  {
    eventType: 'EXPIRATION',
    title: 'RevenueCat - Expiration',
    description:
      'Fires when a subscription actually ends (entitlements should be revoked now).',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(EXPIRATION_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('EXPIRATION', {
      expirationReason: 'BILLING_ERROR',
    }),
    useCases: [
      {
        title: 'Re-engagement campaign',
        description: 'Tease what’s new and offer a return discount.',
      },
    ],
  },
  {
    eventType: 'BILLING_ISSUE',
    title: 'RevenueCat - Billing Issue',
    description: 'Fires when a payment fails (dunning candidate).',
    category: 'billing',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('BILLING_ISSUE', {}),
    useCases: [
      {
        title: 'Dunning email',
        description: 'Ask the user to update their payment method.',
      },
    ],
  },
  {
    eventType: 'SUBSCRIPTION_PAUSED',
    title: 'RevenueCat - Subscription Paused',
    description:
      'Fires when a Google Play subscription is scheduled to pause at the end of its current period. Keep access until `EXPIRATION`.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('SUBSCRIPTION_PAUSED', {
      store: 'PLAY_STORE',
    }),
    useCases: [
      {
        title: 'Pause acknowledgment',
        description: 'Confirm the pause + when access resumes.',
      },
    ],
  },
  {
    eventType: 'SUBSCRIPTION_EXTENDED',
    title: 'RevenueCat - Subscription Extended',
    description:
      'Fires when a store extends the current subscription period or Google Play defers a charge.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(NO_EXTRAS),
    samplePayload: buildSamplePayload('SUBSCRIPTION_EXTENDED', {}),
    useCases: [
      {
        title: 'Courtesy confirmation',
        description: 'Let the user know their subscription was extended.',
      },
    ],
  },
  {
    eventType: 'TRANSFER',
    title: 'RevenueCat - Transfer',
    description:
      'Receives an entitlement transfer. The official TRANSFER payload has app-user IDs but no recipient email, so Brew records it as unresolved and starts no automation.',
    category: 'subscription',
    payloadSchema: buildPayloadSchema(TRANSFER_EXTRA_FIELDS),
    samplePayload: buildSamplePayload('TRANSFER', {}),
    useCases: [
      {
        title: 'Transfer receipt audit',
        description:
          'Confirm the transfer reached Recent Events; no email or ops automation is claimed without a real recipient.',
      },
    ],
  },
]

export function isSupportedRevenueCatEvent(
  eventType: string
): eventType is SupportedRevenueCatEventType {
  return SUPPORTED_REVENUECAT_EVENT_SET.has(eventType)
}
